import React, { useEffect, useState } from 'react';
import {
  Modal, Button, Table, Form, Spinner, Alert, Row, Col,
} from 'react-bootstrap';
import moment from 'moment';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessAlert, showError } from '../../../../core/alert/sweetAlertConfig';

// Reuse types from your existing module or centralize them in a shared file
export interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}
export interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  parkingSlotName?: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}
export interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  responsiblePersonName?: string;
  responsiblePersonContactNumber?: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  createdBy?: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}
export interface GetVisitorByIdResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO;
  total: number | null;
}

export type MarkOutModalProps = {
  show: boolean;
  onHide: () => void;
  visitorRequestId: number | null;
  onSuccess: () => void;
};

const MarkOutModule: React.FC<MarkOutModalProps> = ({
  show,
  onHide,
  visitorRequestId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [details, setDetails] = useState<VisitorRequestDTO | null>(null);
  const [selectedVisitorIds, setSelectedVisitorIds] = useState<number[]>([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
  const [actualOutTime, setActualOutTime] = useState<string>(() =>
    moment().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm')
  );

  useEffect(() => {
    if (!show || !visitorRequestId) return;
    setLoading(true);
    setErrorText(null);
    (async () => {
      try {
        const res: AxiosResponse<GetVisitorByIdResponse> =
          await apiDefinitions.getVisitorById(visitorRequestId);
        if (res.status === 200 && res.data?.data) {
          const data = res.data.data;
          setDetails(data);

          // Pre-select all by default (same as Mark In for consistency)
          const vIds = data.visitors?.map(v => v.id) || [];
          const vehIds = data.vehicles?.map(v => v.id) || [];
          setSelectedVisitorIds(vIds);
          setSelectedVehicleIds(vehIds);
        } else {
          const msg = res.data?.statusDescription || 'Failed to fetch visitor request details.';
          setErrorText(msg);
          showError(msg);
        }
      } catch (e: any) {
        const msg = e?.response?.data?.statusDescription || e?.message || 'Failed to fetch visitor request details.';
        setErrorText(msg);
        showError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [show, visitorRequestId]);

  const toggleVisitor = (id: number) => {
    setSelectedVisitorIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleVehicle = (id: number) => {
    setSelectedVehicleIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!details) return;

    if (selectedVisitorIds.length === 0) {
      const msg = 'Please select at least one visitor.';
      setErrorText(msg);
      showError(msg);
      return;
    }

    // Format to "YYYY-MM-DD HH:mm:ssZ" with Z literal
    const formattedOutTime = moment(actualOutTime).utc().format('YYYY-MM-DD HH:mm:ss[Z]');

    const payload = {
      visitorRequestId: details.id,
      visitorIds: selectedVisitorIds,
      visitorVehicleIds: selectedVehicleIds,
      actualOutTime: formattedOutTime,
    };

    try {
      setSubmitting(true);
      setErrorText(null);
      const res = await apiDefinitions.markVisitorOut(payload);
      if (res.status === 200) {
        showSuccessAlert("Mark Out Successfully."); // swap to a dedicated "Marked Out Successfully" alert if you have one
        onSuccess();
        onHide();
      } else {
        const msg = res.data?.statusDescription || 'Failed to mark visitor(s) out.';
        setErrorText(msg);
        showError(msg);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.statusDescription || e?.message || 'Failed to mark visitor(s) out.';
      setErrorText(msg);
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered autoFocus={false} enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>Mark Visitor(s) Out</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Alert variant="info">Loading request details…</Alert>}
        {errorText && <Alert variant="danger" className="mb-3">{errorText}</Alert>}

        {!loading && details && (
          <>
            {/* Header Summary */}
            <Row className="g-3 mb-2">
              <Col md={6}>
                <Table bordered hover>
                  <tbody>
                    <tr><td><strong>Request #</strong></td><td>{details.id}</td></tr>
                    <tr>
                      <td><strong>Responsible</strong></td>
                      <td>
                        {details.responsiblePersonName || details.responsiblePersonEmpNo}{' '}
                        {details.responsiblePersonContactNumber ? `(${details.responsiblePersonContactNumber})` : ''}
                      </td>
                    </tr>
                    <tr><td><strong>Purpose</strong></td><td>{details.visitorReason || '—'}</td></tr>
                    <tr><td><strong>Position</strong></td><td>{details.visitorPosition || '—'}</td></tr>
                    <tr><td><strong>Status</strong></td><td>{details.status}</td></tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Actual Out Time</strong></td>
                      <td>
                        <Form.Control
                          type="datetime-local"
                          value={actualOutTime}
                          onChange={(e) => setActualOutTime(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr><td><strong>Parking Required</strong></td><td>{details.parkingRequired ? 'Yes' : 'No'}</td></tr>
                    <tr>
                      <td><strong>Created Date</strong></td>
                      <td>{new Date(details.dateTime).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Visitors */}
            <h5 className="mt-2">Visitors</h5>
            <Table bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Select</th>
                  <th>Name</th>
                  <th>NIC</th>
                  <th>Company</th>
                </tr>
              </thead>
              <tbody>
                {details.visitors?.length ? (
                  details.visitors.map((v) => {
                    const checked = selectedVisitorIds.includes(v.id);
                    return (
                      <tr key={v.id}>
                        <td className="text-center">
                          <Form.Check type="checkbox" checked={checked} onChange={() => toggleVisitor(v.id)} />
                        </td>
                        <td>{v.name || 'N/A'}</td>
                        <td>{v.nic || 'N/A'}</td>
                        <td>{v.visitorCompany || 'N/A'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={4} className="text-muted">No visitors found.</td></tr>
                )}
              </tbody>
            </Table>

            {/* Vehicles */}
            <h5 className="mt-4">Vehicles</h5>
            <Table bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Select</th>
                  <th>Vehicle Number</th>
                  <th>Parking Slot</th>
                  <th>Arrival</th>
                  <th>Departure</th>
                </tr>
              </thead>
              <tbody>
                {details.vehicles?.length ? (
                  details.vehicles.map((veh) => (
                    <tr key={veh.id}>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={selectedVehicleIds.includes(veh.id)}
                          onChange={() => toggleVehicle(veh.id)}
                        />
                      </td>
                      <td>{veh.vehicleNumber || 'N/A'}</td>
                      <td>{veh.parkingSlotName || 'N/A'}</td>
                      <td>{veh.arrivalTime ? moment(veh.arrivalTime).format('YYYY-MM-DD HH:mm') : 'N/A'}</td>
                      <td>{veh.departureTime ? moment(veh.departureTime).format('YYYY-MM-DD HH:mm') : 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5} className="text-muted">No vehicles found.</td></tr>
                )}
              </tbody>
            </Table>
          </>
        )}

        {!loading && !details && <Alert variant="warning">No request details available.</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide} disabled={submitting}>Close</Button>
        <Button variant="success" onClick={handleSubmit} disabled={submitting || loading || !details}>
          {submitting ? 'Marking Out…' : 'Mark Out'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarkOutModule;
